import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import useDeferredRowChange from '../useDeferredRowChange';
import type { BombiixCellRendererParams, BombiixRow, BombiixRowId } from '../types';

interface HierActionInputExtras {
  getCanAdd?: (row: BombiixRow | undefined) => boolean;
  getCanDelete?: (row: BombiixRow | undefined) => boolean;
  onAdd?: (row: BombiixRow | undefined) => void;
  onDelete?: (row: BombiixRow | undefined) => void;
  type?: 'text' | 'number';
}

type HierActionInputCellProps = BombiixCellRendererParams<any, string> & HierActionInputExtras;

/**
 * Editable text cell with per-row add/delete icons revealed inline on hover.
 * Keeps a native `<input>` (not NewInput) because the inline add/delete buttons
 * must sit as siblings inside `.cag-hier-input-row`; NewInput's own wrap markup
 * would break that row layout.
 */
function HierActionInputCell(params: HierActionInputCellProps) {
  const { value, data, colDef, onRowChange, getRowId, getCanAdd, getCanDelete, onAdd, onDelete, disabled, error, placeholder, type = 'text' } = params;
  const rowId: BombiixRowId | undefined = getRowId ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const canAdd = typeof getCanAdd === 'function' ? getCanAdd(data) : false;
  const canDelete = typeof getCanDelete === 'function' ? getCanDelete(data) : false;

  const focusedRef = useRef<boolean>(false);
  const [local, setLocal] = useState<string>(value ?? '');
  const [commitChange, flushChange] = useDeferredRowChange(onRowChange);

  useEffect(() => {
    if (!focusedRef.current) setLocal(value ?? '');
  }, [value]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = type === 'number' ? event.target.value.replace(/[^0-9.\-]/g, '') : event.target.value;
    setLocal(nextValue);
    if (rowId !== undefined && field) commitChange(rowId, field, nextValue);
  };

  return (
    <div className={`cag-cell-wrap${error ? ' cag-cell-wrap--error' : ''}`}>
      <div className="cag-hier-input-row">
        {(canAdd || canDelete) && (
          <div className="cag-hier-inline-actions">
            {canAdd && (
              <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" title="Add row" aria-label="Add row" onClick={() => onAdd?.(data)}>
                <i className="bombiix-icon-add" />
              </button>
            )}
            {canDelete && (
              <button type="button" className="bombiix-icon-container bombiix-icon-container--red" title="Delete row" aria-label="Delete row" onClick={() => onDelete?.(data)}>
                <i className="bombiix-icon-delete" />
              </button>
            )}
          </div>
        )}
        <input
          className="cag-cell-input"
          type="text"
          inputMode="decimal"
          value={local}
          placeholder={placeholder ?? colDef?.headerName ?? 'Text Box'}
          disabled={disabled}
          onFocus={() => {
            focusedRef.current = true;
          }}
          onBlur={() => {
            focusedRef.current = false;
            flushChange();
            setLocal(value ?? '');
          }}
          onChange={handleChange}
        />
      </div>
      {error ? <span className="cag-cell-error">{error}</span> : null}
    </div>
  );
}

export default HierActionInputCell;
